import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import {
  Download, ArrowUp, ArrowDown, ArrowUpDown, X, Search, RefreshCw,
} from 'lucide-react';

// iter342 — In-portal Player Summary tab. "Games Played" == games the
// coach selected the player into (RSVP-independent). RSVP columns are
// informational only. Filters + per-tab CSV export live here.

const ALL = '__all__';

const FILTER_DEFAULTS = { age: ALL, team: ALL, search: '', minGames: '' };

const SORT_KEYS = {
  player: (r) => (r.player || '').toLowerCase(),
  age: (r) => (r.age || '').toLowerCase(),
  team: (r) => (r.team || '').toLowerCase(),
  games_played: (r) => r.games_played ?? 0,
  rsvp_yes: (r) => r.rsvp_yes ?? 0,
  rsvp_no: (r) => r.rsvp_no ?? 0,
  rsvp_tentative: (r) => r.rsvp_tentative ?? 0,
};

const csvEscape = (val) => {
  if (val === null || val === undefined) return '';
  const s = String(val);
  return (s.includes(',') || s.includes('"') || s.includes('\n')) ? `"${s.replace(/"/g, '""')}"` : s;
};

const SortableHeader = ({ label, sortKey, sort, setSort, className, testId }) => {
  const active = sort.key === sortKey;
  const Icon = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  const toggle = () => {
    if (!active) return setSort({ key: sortKey, dir: 'asc' });
    if (sort.dir === 'asc') return setSort({ key: sortKey, dir: 'desc' });
    return setSort({ key: null, dir: 'asc' });
  };
  return (
    <TableHead className={className}>
      <button type="button" onClick={toggle}
        className={`inline-flex items-center gap-1 select-none hover:text-blue-500 transition-colors ${active ? 'text-blue-500 dark:text-blue-400 font-semibold' : ''}`}
        data-testid={testId}>
        {label}<Icon className="h-3 w-3 opacity-70" />
      </button>
    </TableHead>
  );
};

export const PlayerSummaryTab = ({ tournament, rows, teams, loading, onRefresh }) => {
  const [filters, setFilters] = useState(FILTER_DEFAULTS);
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const setFilter = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const clearFilters = () => setFilters(FILTER_DEFAULTS);

  const teamNames = useMemo(() => {
    const s = new Set((rows || []).map((r) => r.team).filter(Boolean));
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const { age, team, search, minGames } = filters;
    const q = (search || '').trim().toLowerCase();
    const mg = minGames === '' ? null : Number(minGames);
    return (rows || []).filter((r) => {
      if (age !== ALL && (r.age || '').toUpperCase() !== age) return false;
      if (team !== ALL && r.team !== team) return false;
      if (mg !== null && !Number.isNaN(mg) && (r.games_played ?? 0) < mg) return false;
      if (q && !(r.player || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, filters]);

  const sorted = useMemo(() => {
    if (!sort.key) {
      return [...filtered].sort((a, b) =>
        (a.age || '').localeCompare(b.age || '') ||
        (a.team || '').localeCompare(b.team || '') ||
        (a.player || '').localeCompare(b.player || ''));
    }
    const getter = SORT_KEYS[sort.key];
    if (!getter) return filtered;
    const dm = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = getter(a); const bv = getter(b);
      if (av < bv) return -1 * dm;
      if (av > bv) return 1 * dm;
      return 0;
    });
  }, [filtered, sort]);

  const totalGames = useMemo(() => sorted.reduce((s, r) => s + (r.games_played || 0), 0), [sorted]);

  const chips = [];
  if (filters.age !== ALL) chips.push({ key: 'age', label: `Age: ${filters.age}`, clear: () => setFilter({ age: ALL }) });
  if (filters.team !== ALL) chips.push({ key: 'team', label: `Team: ${filters.team}`, clear: () => setFilter({ team: ALL }) });
  if (filters.minGames !== '') chips.push({ key: 'minGames', label: `Min games: ${filters.minGames}`, clear: () => setFilter({ minGames: '' }) });
  if (filters.search) chips.push({ key: 'search', label: `Search: "${filters.search}"`, clear: () => setFilter({ search: '' }) });

  const handleExportCsv = () => {
    const headers = ['Player', 'Age Group', 'Team', 'Games Played', 'RSVP Yes', 'RSVP No', 'RSVP Tentative', 'Selected but no RSVP', "RSVP'd Yes but not Selected"];
    const lines = [headers.join(',')];
    for (const r of sorted) {
      lines.push([
        csvEscape(r.player), csvEscape(r.age), csvEscape(r.team),
        r.games_played ?? 0, r.rsvp_yes ?? 0, r.rsvp_no ?? 0, r.rsvp_tentative ?? 0,
        r.selected_no_rsvp ?? 0, r.rsvp_yes_not_selected ?? 0,
      ].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const slug = (tournament?.name || 'tournament').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    a.download = `${slug}-player-summary-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center flex-wrap gap-2">
          <div>
            <CardTitle>Player Summary</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Games Played counts games a coach selected the player into — independent of RSVP. All game types count.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="ghost" onClick={onRefresh} disabled={loading} data-testid="player-summary-refresh-btn">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
            </Button>
            <Button size="sm" variant="outline" onClick={handleExportCsv} disabled={sorted.length === 0} data-testid="player-summary-export-csv-btn">
              <Download className="h-4 w-4 mr-1" />Export CSV
            </Button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-end gap-2 pt-3 border-t">
          <div className="flex flex-col gap-1">
            <Label className="text-[11px] text-muted-foreground uppercase tracking-wide">Age</Label>
            <Select value={filters.age} onValueChange={(v) => setFilter({ age: v })}>
              <SelectTrigger className="w-32 h-9" data-testid="player-summary-filter-age"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All ages</SelectItem>
                {(tournament?.age_categories || []).map((cat) => (
                  <SelectItem key={cat} value={cat.toUpperCase()}>{cat.toUpperCase()}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-[11px] text-muted-foreground uppercase tracking-wide">Team</Label>
            <Select value={filters.team} onValueChange={(v) => setFilter({ team: v })}>
              <SelectTrigger className="w-44 h-9" data-testid="player-summary-filter-team"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All teams</SelectItem>
                {teamNames.map((n) => (<SelectItem key={n} value={n}>{n}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-[11px] text-muted-foreground uppercase tracking-wide">Min games</Label>
            <Input type="number" min="0" placeholder="0" value={filters.minGames}
              onChange={(e) => setFilter({ minGames: e.target.value })} className="w-24 h-9"
              data-testid="player-summary-filter-min-games" />
          </div>
          <div className="flex flex-col gap-1 grow min-w-[180px]">
            <Label className="text-[11px] text-muted-foreground uppercase tracking-wide">Search</Label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input type="text" placeholder="Player name…" value={filters.search}
                onChange={(e) => setFilter({ search: e.target.value })} className="pl-7 h-9"
                data-testid="player-summary-filter-search" />
            </div>
          </div>
        </div>

        {chips.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Active filters:</span>
            {chips.map((chip) => (
              <button key={chip.key} type="button" onClick={chip.clear}
                className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs px-2 py-0.5 hover:bg-blue-100 transition-colors"
                data-testid={`player-summary-chip-${chip.key}`}>
                {chip.label}<X className="h-3 w-3" />
              </button>
            ))}
            <button type="button" onClick={clearFilters} className="text-xs text-muted-foreground underline hover:text-foreground" data-testid="player-summary-clear-all">Clear all</button>
          </div>
        )}
      </CardHeader>

      <CardContent>
        {(rows || []).length > 0 && (
          <div className="mb-2 text-xs text-muted-foreground flex justify-between">
            <span data-testid="player-summary-result-count">Showing {sorted.length} of {(rows || []).length} rows</span>
            <span className="font-medium text-foreground" data-testid="player-summary-total-games">Total games played: {totalGames}</span>
          </div>
        )}
        {sorted.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground" data-testid="player-summary-empty">
            {(rows || []).length === 0 ? 'No games / selections recorded yet' : 'No players match the current filters'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader label="Player" sortKey="player" sort={sort} setSort={setSort} testId="ps-sort-player" />
                  <SortableHeader label="Age Group" sortKey="age" sort={sort} setSort={setSort} testId="ps-sort-age" />
                  <SortableHeader label="Team" sortKey="team" sort={sort} setSort={setSort} testId="ps-sort-team" />
                  <SortableHeader label="Games Played" sortKey="games_played" sort={sort} setSort={setSort} className="text-right" testId="ps-sort-games" />
                  <SortableHeader label="RSVP Yes" sortKey="rsvp_yes" sort={sort} setSort={setSort} className="text-right" testId="ps-sort-yes" />
                  <SortableHeader label="RSVP No" sortKey="rsvp_no" sort={sort} setSort={setSort} className="text-right" testId="ps-sort-no" />
                  <SortableHeader label="RSVP Tentative" sortKey="rsvp_tentative" sort={sort} setSort={setSort} className="text-right" testId="ps-sort-tentative" />
                  <TableHead className="text-right">Selected · no RSVP</TableHead>
                  <TableHead className="text-right">RSVP Yes · not Selected</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((r, i) => (
                  <TableRow key={`${r.player}-${r.team}-${i}`} data-testid={`player-summary-row-${i}`}>
                    <TableCell className="font-medium">{r.player}</TableCell>
                    <TableCell>{r.age}</TableCell>
                    <TableCell>{r.team}</TableCell>
                    <TableCell className="text-right font-semibold text-foreground">{r.games_played}</TableCell>
                    <TableCell className="text-right text-emerald-600 dark:text-emerald-400">{r.rsvp_yes}</TableCell>
                    <TableCell className="text-right text-red-600 dark:text-red-400">{r.rsvp_no}</TableCell>
                    <TableCell className="text-right text-amber-600 dark:text-amber-400">{r.rsvp_tentative}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{r.selected_no_rsvp}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{r.rsvp_yes_not_selected}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
